import { Box, Card, CardContent, CardHeader, CircularProgress, Divider, Grid, Paper, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import AppBreadcrumbs from "../../../app/components/AppBreadcrumbs";
import AppTimeline, { Align, TimeLineElement } from "../../../app/components/AppTimeLine";
import NotFound from "../../../app/errors/NotFound";
import LoadingComponent from "../../../app/layout/LoadingComponent";
import { useAppDispatch, useAppSelector } from "../../../app/store/configureStore";
import AdminHelpInInvestmentChat from "./AdminHelpInInvestmentChat";
import { fetchAdminInvestmentAsync, adminInvestmentsSelectors } from "./adminInvestmentsSlice";
import { formatToLocalDate, formatToLocalTime, getInvestmentStatusColor, getInvestmentStatusTitle } from "../../../app/util/util";
import { Investment } from "../../../app/models/investment";
import agent from "../../../app/api/agent";
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import CancelIcon from '@mui/icons-material/Cancel';
import DoneIcon from '@mui/icons-material/Done';
import DoneOutlineIcon from '@mui/icons-material/DoneOutline';
import GradingIcon from '@mui/icons-material/Grading';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import AppReturn from "../../../app/components/AppReturn";

export default function AdminInvestmentDetails() {
    const dispatch = useAppDispatch();
    const { id } = useParams<{ id: string }>();
    const adminInvestment = useAppSelector(state => adminInvestmentsSelectors.selectById(state, id!));
    const { status: adminInvestmentStatus } = useAppSelector(state => state.adminInvestments);
    const [timeLineElements, setTimeLineElements] = useState<TimeLineElement[]>([]);
    const [loading, setLoading] = useState(false);

    function getStatusIcon(currentState: number) {
        switch (currentState) {
            case 0:
                return <HourglassTopIcon />;
            case 1:
                return <DoneIcon />;
            case 2:
                return <CancelIcon />;
            case 3:
                return <DoneOutlineIcon />;
            case 4:
                return <CancelIcon />;
            case 5:
                return <GradingIcon />;
            case 6:
                return <DoneAllIcon />;
            case 7:
                return <CancelIcon />;
            default:
                return <CancelIcon />;
        }
    }

    useEffect(() => {
        if (!adminInvestment) dispatch(fetchAdminInvestmentAsync(parseInt(id!)))
    }, [id, dispatch, adminInvestment])

    useEffect(() => {
        const fetchHistory = async () => {
            if (adminInvestment) {
                setLoading(true);
                try {
                    const historyOfInvestment: Investment[] = await agent.Admin.Investments.getHistoryOfInvestment(adminInvestment.publicId);
                    const timelineData: TimeLineElement[] = historyOfInvestment.map(h => ({
                        dateAndTimeToDisplay: new Date(h.updatingDateTime).toLocaleString(),
                        align: Align.Left,
                        header: h.updatingUserIsAdmin ? 'Administrator' : 'Klient',
                        subtitle: getInvestmentStatusTitle(h.state),
                        icon: getStatusIcon(h.state),
                        iconColor: getInvestmentStatusColor(h.state)
                    }));
                    setTimeLineElements(timelineData);
                } catch (error) {
                    console.error("Failed to fetch investment history:", error);
                } finally {
                    setLoading(false);
                }
            }
        };
        fetchHistory();
    }, [adminInvestment]);

    if (adminInvestmentStatus.includes('pending')) return <LoadingComponent />

    if (!adminInvestment) return <NotFound />

    return (
        <>
            <AppBreadcrumbs />
            <AppReturn
                title={"Projekt inwestycyjny #" + adminInvestment.publicId}
                backPath="/admin/investments"
            />

            <Grid container>
                <Grid item md={7} sm={12}>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Podstawowe informacje'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item sm={6} xs={12}>
                                    <Typography variant="h5">
                                        {adminInvestment.investmentName}
                                    </Typography>
                                    <Typography variant="overline">
                                        nazwa wskazana przez Klienta
                                    </Typography>
                                </Grid>
                                <Grid item sm={6} xs={12}>
                                    <Typography variant="h5">
                                        {formatToLocalDate(adminInvestment.investmentCreatedDateTime.toString())} {formatToLocalTime(adminInvestment.investmentCreatedDateTime.toString())}
                                    </Typography>
                                    <Typography variant="overline">
                                        data i godzina złożenia zamówienia
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {adminInvestment.competition}
                                    </Typography>
                                    <Typography variant="overline">
                                        Rodzaj naboru/konkursu lub link do naboru
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {adminInvestment.clientComment}
                                    </Typography>
                                    <Typography variant="overline">
                                        komentarz Klienta
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {adminInvestment.id}
                                    </Typography>
                                    <Typography variant="overline">
                                        tymczasowe Id
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="h6">
                                        {getInvestmentStatusTitle(adminInvestment.state)}
                                    </Typography>
                                    <Typography variant="overline">
                                        Status zamówienia
                                    </Typography>
                                </Grid>
                                {adminInvestment.state === 1 || adminInvestment.state === 3 || adminInvestment.state === 4 || adminInvestment.state === 5 ?
                                    (<>
                                        <Grid item xs={12}>
                                            <Divider sx={{ mb: 2 }} />
                                            <Typography variant="h6">
                                                {adminInvestment.publicComment}
                                            </Typography>
                                            <Typography variant="overline">
                                                Komentarz wyceniającego
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h6">
                                                {adminInvestment.price}
                                            </Typography>
                                            <Typography variant="overline">
                                                Wycena
                                            </Typography>
                                        </Grid>
                                    </>) : ''
                                }
                                {adminInvestment.state === 5 ?
                                    <>
                                        <Grid item xs={12}>
                                            <Divider sx={{ mb: 2 }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientInstitutionName}
                                            </Typography>
                                            <Typography variant="overline">
                                                Nazwa instytucji
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientLastname}
                                            </Typography>
                                            <Typography variant="overline">
                                                Imię
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientFirstname}
                                            </Typography>
                                            <Typography variant="overline">
                                                Nazwisko
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientPhone}
                                            </Typography>
                                            <Typography variant="overline">
                                                Telefon
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientEmail}
                                            </Typography>
                                            <Typography variant="overline">
                                                Email
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientStreet}
                                            </Typography>
                                            <Typography variant="overline">
                                                Ulica
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientBuilding}
                                            </Typography>
                                            <Typography variant="overline">
                                                Budynek
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientPremises}
                                            </Typography>
                                            <Typography variant="overline">
                                                Lokal
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientZip}
                                            </Typography>
                                            <Typography variant="overline">
                                                Kod pocztowy
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientCity}
                                            </Typography>
                                            <Typography variant="overline">
                                                Miasto
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientState}
                                            </Typography>
                                            <Typography variant="overline">
                                                Województwo
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientCountry}
                                            </Typography>
                                            <Typography variant="overline">
                                                Kraj
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Divider sx={{ mb: 2 }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h5">
                                                {adminInvestment.payerInstitutionName}
                                            </Typography>
                                            <Typography variant="overline">
                                                Nazwa instytucji płatnika
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h5">
                                                {adminInvestment.payerRepresentative}
                                            </Typography>
                                            <Typography variant="overline">
                                                Przedstawiciel
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.payerStreet}
                                            </Typography>
                                            <Typography variant="overline">
                                                Ulica
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography variant="h5">
                                                {adminInvestment.payerBuilding}
                                            </Typography>
                                            <Typography variant="overline">
                                                Budynek
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={3}>
                                            <Typography variant="h5">
                                                {adminInvestment.payerPremises}
                                            </Typography>
                                            <Typography variant="overline">
                                                Lokal
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.payerZip}
                                            </Typography>
                                            <Typography variant="overline">
                                                Kod pocztowy
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.payerCity}
                                            </Typography>
                                            <Typography variant="overline">
                                                Miasto
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.payerState}
                                            </Typography>
                                            <Typography variant="overline">
                                                Województwo
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.payerCountry}
                                            </Typography>
                                            <Typography variant="overline">
                                                Kraj
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {adminInvestment.payerNIP}
                                            </Typography>
                                            <Typography variant="overline">
                                                NIP
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Divider sx={{ mb: 2 }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h5">
                                                {adminInvestment.projectName}
                                            </Typography>
                                            <Typography variant="overline">
                                                Nazwa projektu
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {formatToLocalDate(adminInvestment.projectDateStart.toString())} {formatToLocalTime(adminInvestment.projectDateStart.toString())}
                                            </Typography>
                                            <Typography variant="overline">
                                                Data rozpoczęcia projektu
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Typography variant="h5">
                                                {formatToLocalDate(adminInvestment.projectDateEnd.toString())} {formatToLocalTime(adminInvestment.projectDateEnd.toString())}
                                            </Typography>
                                            <Typography variant="overline">
                                                Data zakończenia projektu
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h5">
                                                {adminInvestment.projectMaxBudget}
                                            </Typography>
                                            <Typography variant="overline">
                                                Maksymalny budżet
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h5">
                                                {adminInvestment.projectDescription}
                                            </Typography>
                                            <Typography variant="overline">
                                                Opis projektu
                                            </Typography>
                                        </Grid>

                                        <Grid item xs={12}>
                                            <Divider sx={{ mb: 2 }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientExperience}
                                            </Typography>
                                            <Typography variant="overline">
                                                Doświadczenie klienta
                                            </Typography>
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Typography variant="h5">
                                                {adminInvestment.clientPotential}
                                            </Typography>
                                            <Typography variant="overline">
                                                Potencjał klienta
                                            </Typography>
                                        </Grid>
                                    </> : ''
                                }
                            </Grid>
                        </CardContent>
                    </Card>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Dane klienta'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {adminInvestment.userDisplayName}
                                    </Typography>
                                    <Typography variant="overline">
                                        nazwa Klienta
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h6">
                                        {adminInvestment.userEmail}
                                    </Typography>
                                    <Typography variant="overline">
                                        email
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="h5">
                                        {adminInvestment.userPhone === 'null' || adminInvestment.userPhone === '' ?
                                            '-' : adminInvestment.userPhone}
                                    </Typography>
                                    <Typography variant="overline">
                                        telefon kontaktowy
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} sm={12} md={5} >
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Historia'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <Grid container>
                            <Grid item xs={12}>

                            </Grid>
                            <Grid item xs={12}>
                                {loading ? (
                                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                                        <CircularProgress />
                                    </Box>
                                ) : <AppTimeline elements={timeLineElements} />}
                            </Grid>
                        </Grid>
                    </Card>
                    <Card component={Paper} sx={{ p: 3, mb: 2, mr: { xs: 0, md: 2 } }}>
                        <CardHeader title='Korespondencja'
                            titleTypographyProps={{
                                sx: { fontWeight: 'bold', color: 'secondary' }
                            }} />
                        <Divider />
                        <Grid container>
                            <Grid item xs={12}>

                            </Grid>
                            <Grid item xs={12}>
                                <AdminHelpInInvestmentChat investmentId={parseInt(id!)} />
                            </Grid>
                        </Grid>
                    </Card>
                </Grid>
            </Grid>
        </>
    )
}